import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Users, FileText, Inbox, Receipt, TrendingDown, ArrowRight } from "lucide-react";

const FUNNEL_LABELS = {
  signup_completed: "Signed up",
  dive_brain_completed: "Brain done",
  strategy_onboarding_completed: "Strategy done",
  foundation_approved: "Foundation ok",
  first_calendar_done: "First post",
  trial_converted: "Converted",
};

// Where the "Nudge stuck →" button on each row should route.
// The key is the row's event; the value is the URL param handled by AdminBroadcast.
const NUDGE_TARGET = {
  dive_brain_completed: "signed_up_no_brain",       // stuck BEFORE this row
  strategy_onboarding_completed: "brain_no_strategy",
  foundation_approved: "strategy_no_foundation",
  first_calendar_done: "foundation_no_action",
  trial_converted: "acted_no_convert",
};

function FunnelWidget({ funnel }) {
  const order = funnel.order || [];
  const max = Math.max(...order.map((k) => funnel.counts?.[k] || 0), 1);
  const rateKeys = [
    ["signup_to_brain", "→ Brain"],
    ["brain_to_strategy_completed", "→ Strategy"],
    ["strategy_completed_to_foundation_approved", "→ Foundation"],
    ["foundation_approved_to_first_action", "→ First post"],
    ["first_action_to_trial_converted", "→ Paid"],
  ];
  // Find weakest link — the lowest rate
  let weakest = null;
  let weakestRate = 101;
  rateKeys.forEach(([k, l]) => {
    const r = funnel.rates?.[k];
    if (r !== undefined && r < weakestRate && (funnel.counts?.[order[rateKeys.findIndex(([kk]) => kk === k)]] || 0) > 0) {
      weakest = l; weakestRate = r;
    }
  });

  return (
    <div className="dms-card p-5" data-testid="admin-funnel-widget">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="dms-label">Signup → Paid funnel</div>
          <div className="text-[11.5px] text-[#94A3B8] mt-0.5">
            End-to-end signup-to-paid: <strong className="text-[#0A2342]">{funnel.rates?.overall_signup_to_paid ?? 0}%</strong>
          </div>
        </div>
        <Link
          to="/admin/broadcast"
          data-testid="funnel-widget-nudge-cta"
          className="text-[11.5px] font-semibold text-[#1D4E89] hover:text-[#0A2342] inline-flex items-center gap-1"
        >Nudge stuck users <ArrowRight className="w-3 h-3" /></Link>
      </div>

      <div className="space-y-2">
        {order.map((key, idx) => {
          const count = funnel.counts?.[key] || 0;
          const pct = Math.round((count / max) * 100);
          const rateKey = rateKeys[idx - 1]?.[0];
          const conv = rateKey ? funnel.rates?.[rateKey] : null;
          const nudgeStage = NUDGE_TARGET[key];
          const prevCount = idx > 0 ? funnel.counts?.[order[idx - 1]] || 0 : 0;
          const stuckCount = Math.max(0, prevCount - count);
          return (
            <div key={key}>
              <div className="flex items-center justify-between text-[12px] mb-1">
                <span className="font-semibold text-[#1F2937]">{FUNNEL_LABELS[key] || key}</span>
                <span className="text-[#64748B] flex items-center gap-2">
                  <strong className="text-[#0A2342]">{count}</strong>
                  {conv !== null && conv !== undefined && (
                    <span className={`text-[10.5px] font-bold ${conv < 30 ? "text-[#B91C1C]" : conv < 60 ? "text-[#B45309]" : "text-[#0F766E]"}`}>
                      {conv}%
                    </span>
                  )}
                  {nudgeStage && stuckCount > 0 && (
                    <Link
                      to={`/admin/broadcast?stuck=${nudgeStage}`}
                      data-testid={`nudge-${nudgeStage}`}
                      title={`Broadcast to the ${stuckCount} users stuck before this step`}
                      className="ml-1 inline-flex items-center gap-0.5 rounded-md bg-[#0A2342] text-white px-2 py-0.5 text-[10.5px] font-semibold hover:bg-[#0F2D50] transition-colors"
                    >
                      Nudge {stuckCount} <ArrowRight className="w-2.5 h-2.5" />
                    </Link>
                  )}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-[#F1F5F9] overflow-hidden">
                <div className="h-full bg-[#0A2342] transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      {weakest && weakestRate < 60 && (
        <div className="mt-4 flex items-start gap-2 text-[11.5px] text-[#B45309] bg-[#FEF3C7] border border-[#FCD34D] rounded-md px-2.5 py-2">
          <TrendingDown className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Weakest transition: <strong>{weakest}</strong> at {weakestRate}%. Consider a broadcast targeting the stuck users on this step.</span>
        </div>
      )}
    </div>
  );
}

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/funnel/summary").then(({ data }) => setFunnel(data)).catch(() => {});
  }, []);
  if (!stats) return <div className="text-[#64748B]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>;

  return (
    <div data-testid="admin-overview">
      <div className="dms-label mb-3">Overview</div>
      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#0A2342]">Dashboard</h1>
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { icon: Users, label: "Total users", value: stats.users.total },
          { icon: Users, label: "Onboarded", value: stats.users.onboarded },
          { icon: Receipt, label: "Active subs", value: stats.users.active },
          { icon: Users, label: "Trialing", value: stats.users.trialing },
          { icon: FileText, label: "Vault items", value: stats.vault_items },
          { icon: Receipt, label: "Paid txns", value: stats.paid_transactions },
          { icon: Inbox, label: "Unread msgs", value: stats.unread_messages },
        ].map(({ icon: Icon, label, value }) => (
          <div key={label} className="dms-card p-4 sm:p-5">
            <Icon className="w-4 h-4 text-[#1D4E89]" />
            <div className="mt-3 dms-label">{label}</div>
            <div className="mt-1 font-heading font-bold text-2xl text-[#0A2342]">{value}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 dms-card p-5">
        <div className="dms-label mb-3">Users by tier</div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(stats.users.by_tier || {}).map(([tier, count]) => (
            <span key={tier} className="dms-badge">{tier}: {count}</span>
          ))}
        </div>
      </div>

      {funnel && <div className="mt-6"><FunnelWidget funnel={funnel} /></div>}
    </div>
  );
}
